import React, { useState, useEffect } from 'react';
import { Save, X, Eye, EyeOff, ChevronDown, ChevronRight } from 'lucide-react';
import { saveSchemaConfig } from '../../utils/api';

const SchemaJsonEditor = ({ initialConfig, hotelId: hotelIdProp, fileType: fileTypeProp, onClose, onSave }) => {
  const [config, setConfig] = useState(initialConfig);
  const [editingConfig, setEditingConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showRawJson, setShowRawJson] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});

  useEffect(() => {
    setConfig(initialConfig);
    setEditingConfig(JSON.parse(JSON.stringify(initialConfig)));
  }, [initialConfig]);

  const handleSave = async () => {
    if (!editingConfig) return;

    try {
      setSaving(true);
      setError(null);

      // If in JSON view, parse the string first
      let configToSave = editingConfig;
      if (showRawJson && typeof editingConfig === 'string') {
        try {
          configToSave = JSON.parse(editingConfig);
        } catch (parseErr) {
          setError('Invalid JSON format. Please fix the JSON syntax before saving.');
          setSaving(false);
          return;
        }
      }

      // Extract hotelId and fileType - handle different field names
      // Fall back to the feed row the editor was opened from — saved schema
      // documents don't always carry their own hotelId/fileType.
      const hotelId = configToSave.hotelId || configToSave.locationId || initialConfig?.hotelId || initialConfig?.locationId || hotelIdProp;
      const fileType = fileTypeProp || configToSave.fileType || initialConfig?.fileType || configToSave.auditFile || configToSave.etlFunction || 'audit';
      
      if (!hotelId) {
        setError('Unable to determine hotel/location ID from configuration');
        setSaving(false);
        return;
      }

      // Ensure the config has the required fields before saving
      const finalConfig = {
        ...configToSave,
        hotelId: hotelId,
        fileType: fileType
      };

      // Save the configuration with extracted values
      const response = await saveSchemaConfig(hotelId, fileType, finalConfig);
      
      if (response && response.success) {
        setConfig(configToSave);
        if (onSave) {
          onSave(configToSave);
        }
        onClose();
      } else {
        throw new Error(response?.message || 'Failed to save configuration');
      }
    } catch (err) {
      setError('Failed to save configuration: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const updateConfigValue = (path, value) => {
    if (!editingConfig || typeof editingConfig !== 'object') return;

    const pathArray = path.split('.');
    const newConfig = JSON.parse(JSON.stringify(editingConfig));
    
    let current = newConfig;
    for (let i = 0; i < pathArray.length - 1; i++) {
      if (current[pathArray[i]] === undefined) {
        current[pathArray[i]] = {};
      }
      current = current[pathArray[i]];
    }
    
    current[pathArray[pathArray.length - 1]] = value;
    setEditingConfig(newConfig);
  };

  const toggleSection = (sectionName) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionName]: !prev[sectionName]
    }));
  };

  const renderConfigField = (key, value, path = '') => {
    const fullPath = path ? `${path}.${key}` : key;
    
    // Skip internal fields
    if (['_rid', '_self', '_etag', '_attachments', '_ts', 'id'].includes(key)) {
      return null;
    }
    
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      const isExpanded = expandedSections[fullPath] !== false;
      
      return (
        <div key={key} className="mb-4">
          <button
            onClick={() => toggleSection(fullPath)}
            className="flex items-center w-full text-left p-2 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
          >
            {isExpanded ? (
              <ChevronDown className="w-4 h-4 mr-2" />
            ) : (
              <ChevronRight className="w-4 h-4 mr-2" />
            )}
            <span className="font-medium text-gray-700 dark:text-gray-300 capitalize">
              {key.replace(/([A-Z])/g, ' $1').trim()}
            </span>
          </button>
          
          {isExpanded && (
            <div className="ml-6 mt-2 space-y-2">
              {Object.entries(value).map(([subKey, subValue]) =>
                renderConfigField(subKey, subValue, fullPath)
              )}
            </div>
          )}
        </div>
      );
    }
    
    if (Array.isArray(value)) {
      return (
        <div key={key} className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 capitalize">
            {key.replace(/([A-Z])/g, ' $1').trim()}
          </label>
          <div className="space-y-1">
            {value.length === 0 ? (
              <input
                type="text"
                placeholder="Empty array - add items"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400"
                disabled
              />
            ) : (
              value.map((item, index) => (
                <input
                  key={index}
                  type="text"
                  value={typeof item === 'object' ? JSON.stringify(item) : item}
                  onChange={(e) => {
                    const newArray = [...value];
                    try {
                      newArray[index] = e.target.value.startsWith('{') || e.target.value.startsWith('[') 
                        ? JSON.parse(e.target.value) 
                        : e.target.value;
                      updateConfigValue(fullPath, newArray);
                    } catch (err) {
                      newArray[index] = e.target.value;
                      updateConfigValue(fullPath, newArray);
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                />
              ))
            )}
          </div>
        </div>
      );
    }
    
    // Simple field
    return (
      <div key={key} className="mb-4">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 capitalize">
          {key.replace(/([A-Z])/g, ' $1').trim()}
        </label>
        <input
          type={typeof value === 'number' ? 'number' : 'text'}
          value={value || ''}
          onChange={(e) => {
            const newValue = typeof value === 'number' 
              ? parseInt(e.target.value) || 0 
              : e.target.value;
            updateConfigValue(fullPath, newValue);
          }}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
        />
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Edit Schema Configuration
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Hotel: {config?.hotelCode || config?.hotelId || config?.locationId || 'N/A'} | Type: {config?.fileType || config?.etlFunction || config?.auditFile || 'N/A'}
            </p>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => {
                // When switching views, convert between string and object
                if (showRawJson && typeof editingConfig === 'string') {
                  // Switching from JSON to Form view - try to parse
                  try {
                    const parsed = JSON.parse(editingConfig);
                    setEditingConfig(parsed);
                    setError(null);
                  } catch (err) {
                    setError('Invalid JSON format. Please fix before switching to Form View.');
                    return; // Don't switch views if JSON is invalid
                  }
                } else if (!showRawJson && typeof editingConfig === 'object') {
                  // Switching from Form to JSON view - stringify
                  setEditingConfig(JSON.stringify(editingConfig, null, 2));
                }
                setShowRawJson(!showRawJson);
              }}
              className="flex items-center px-3 py-2 text-sm bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
            >
              {showRawJson ? <EyeOff className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
              {showRawJson ? 'Form View' : 'JSON View'}
            </button>
            
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {error && (
          <div className="mx-6 mt-4 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-md">
            <p className="text-red-700 dark:text-red-300">{error}</p>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {showRawJson ? (
            <textarea
              value={typeof editingConfig === 'string' ? editingConfig : JSON.stringify(editingConfig, null, 2)}
              onChange={(e) => {
                // Just store the raw text, don't validate yet
                setEditingConfig(e.target.value);
                setError(null); // Clear any previous errors when typing
              }}
              className="w-full h-full font-mono text-sm p-4 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-900 dark:text-white resize-none"
            />
          ) : (
            <div className="space-y-4">
              {editingConfig && typeof editingConfig === 'object' && Object.entries(editingConfig)
                .filter(([key]) => !['_rid', '_self', '_etag', '_attachments', '_ts', 'id'].includes(key))
                .map(([key, value]) => renderConfigField(key, value))
              }
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end p-6 border-t border-gray-200 dark:border-gray-700 space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors disabled:opacity-50"
          >
            <Save className={`w-4 h-4 mr-1 ${saving ? 'animate-spin' : ''}`} />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SchemaJsonEditor;